import type React from 'react';
import { Children, Fragment, isValidElement, useEffect } from 'react';
import { Box, Button, ButtonGroup, TextButton } from '@zillow/constellation-10';

import PaymentsModal from '@common/components/ConstellationV10Modal';
import { useWizardEditOverlay, useWizardShell } from './context';

type BackAction = {
  onClick: () => void;
  disabled?: boolean;
};

type Props = {
  body: React.ReactNode;
  /** Secondary content (FAQs, tips) shown in the shell's right rail; below the body in the standalone modal. */
  aside?: React.ReactNode;
  /** Escape hatch: rendered verbatim in both contexts. Prefer backAction/footerEnd. */
  footer?: React.ReactNode;
  /** Renders a standard Back button: TextButton in the standalone modal, outlined left-aligned in the shell. */
  backAction?: BackAction;
  /** Primary (and any secondary) actions, rendered after Back. */
  footerEnd?: React.ReactNode;
  footerAriaLabel?: string;
  header: React.ReactNode;
  persona: string;
  dividers?: boolean;
  onRequestClose?: () => void;
  onOpenChange?: () => void;
  size?: Record<string, unknown>;
};

/* Conditional actions passed as a fragment (primary + editMode Cancel) are flattened so only
   what actually renders lands in the ButtonGroup. */
const toActions = (footerEnd?: React.ReactNode): Array<React.ReactNode> => {
  if (isValidElement(footerEnd) && footerEnd.type === Fragment) {
    return Children.toArray((footerEnd.props as { children?: React.ReactNode }).children);
  }
  return Children.toArray(footerEnd);
};

const FOOTER_BUTTON_MIN_WIDTH = '164px';

const buildShellFooter = (
  backAction: BackAction | undefined,
  footerEnd: React.ReactNode,
  footerAriaLabel: string | undefined,
): React.ReactNode => {
  const actions = toActions(footerEnd);
  if (!backAction && actions.length === 0) {
    return null;
  }
  return (
    <ButtonGroup
      aria-label={footerAriaLabel ?? 'Wizard navigation'}
      fluid={{ base: true, lg: false }}
      css={{
        width: '100%',
        justifyContent: backAction ? 'space-between' : 'flex-end',
        '& > *': { minWidth: { lg: FOOTER_BUTTON_MIN_WIDTH } },
      }}
    >
      {backAction && (
        <Button tone="neutral" emphasis="outlined" onClick={backAction.onClick} disabled={backAction.disabled}>
          Back
        </Button>
      )}
      {actions}
    </ButtonGroup>
  );
};

const buildFallbackFooter = (
  backAction: BackAction | undefined,
  footerEnd: React.ReactNode,
  footerAriaLabel: string | undefined,
): React.ReactNode => {
  const actions = toActions(footerEnd);
  const backButton = backAction && (
    <TextButton onClick={backAction.onClick} disabled={backAction.disabled}>
      Back
    </TextButton>
  );
  const buttonCount = (backAction ? 1 : 0) + actions.length;
  if (buttonCount === 0) {
    return null;
  }
  if (buttonCount === 1) {
    return backButton ?? actions[0];
  }
  return (
    <ButtonGroup aria-label={footerAriaLabel ?? 'Wizard actions'}>
      {backButton}
      {actions}
    </ButtonGroup>
  );
};

/**
 * Drop-in replacement for PaymentsModal on wizard screens. Inside a WizardShell it renders
 * the body inline and hands the footer to the shell's footer slot, so navigating between
 * screens never remounts the modal. Outside a shell (editMode, fromDashboard) it falls back
 * to the standalone md-sized PaymentsModal unchanged.
 */
const WizardScreen = (props: Props): JSX.Element => {
  const shell = useWizardShell();
  const editOverlay = useWizardEditOverlay();
  const { aside, body, footer, footerEnd, footerAriaLabel } = props;
  const backAction = editOverlay ? undefined : props.backAction;

  useEffect(() => {
    if (!shell) {
      return undefined;
    }
    shell.setFooter(footer ?? buildShellFooter(backAction, footerEnd, footerAriaLabel));
    shell.setAside(aside ?? null);
    return () => {
      shell.setFooter(null);
      shell.setAside(null);
    };
  }, [shell, aside, footer, backAction, footerEnd, footerAriaLabel]);

  if (!shell) {
    const fallbackBody = aside ? (
      <>
        {body}
        <Box css={{ marginTop: 'default' }}>{aside}</Box>
      </>
    ) : (
      body
    );
    return (
      <PaymentsModal
        {...props}
        header={editOverlay?.title ?? props.header}
        onRequestClose={editOverlay ? editOverlay.onClose : props.onRequestClose}
        body={fallbackBody}
        footer={footer ?? buildFallbackFooter(backAction, footerEnd, footerAriaLabel)}
      />
    );
  }

  return <>{body}</>;
};

export default WizardScreen;
